import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getActiveSession } from '../api/pos.api.js';
import { esComandera } from './deviceModeStore.js';

// Sesión de caja activa — persiste para sobrevivir cierre accidental de la app
export const useSessionStore = create(
  persist(
    (set, get) => ({
      session:    null,   // PosSession document del backend
      terminal:   null,   // PosTerminal document del backend

      // Por qué se acabó el turno, cuando no lo cerró quien está enfrente.
      // Vive aquí y no en el state de la navegación porque al quedarse sin
      // sesión `SessionRoute` redirige por su cuenta a /open-session, y esa
      // redirección pisa cualquier state que quisiéramos llevar. El aviso
      // pertenece al hecho de que la sesión murió, no a una transición.
      // No se persiste, y `openSession` lo borra: en cuanto hay turno nuevo el
      // aviso ya no dice nada.
      aviso: null,

      openSession: ({ session, terminal }) =>
        set({ session, terminal, aviso: null }),

      closeSession: (aviso = null) =>
        set({ session: null, aviso }),

      updateSessionTotals: ({ sales_count, sales_total, expected_cash }) => {
        const s = get().session;
        if (!s) return;
        set({ session: { ...s, sales_count, sales_total, expected_cash: expected_cash ?? s.expected_cash } });
      },

      setTerminal: (terminal) => set({ terminal }),

      // Le pregunta al servidor si el turno que esta tablet cree tener sigue
      // abierto. Sin esto el POS confía en lo que guardó y se entera hasta que
      // cobra — con la venta ya capturada.
      //
      // La regla que no se puede saltar: SOLO se suelta la sesión cuando el
      // servidor contesta y dice que no hay ninguna abierta. Si no se pudo
      // preguntar —sin internet, el hub apagado— se deja como está: una tablet
      // sin red tiene que poder seguir vendiendo.
      revalidar: async () => {
        const { session, terminal } = get();
        // La comandera no tiene cajón que cuadrar, así que no tiene turno.
        if (esComandera()) return;
        if (!session || !terminal?._id) return;

        let abierta;
        try {
          abierta = await getActiveSession(terminal._id);
        } catch {
          return;   // no se pudo preguntar: no se toca nada
        }

        if (!abierta) {
          set({
            session: null,
            aviso:   'La caja se cerró desde otro lado. Abre turno para seguir cobrando.',
          });
          return;
        }

        // Hay caja abierta pero es otra: alguien cerró aquella y abrió esta. No
        // hay por qué mandarlo a abrir turno —ya está abierto— así que se adopta.
        if (String(abierta._id) !== String(session._id)) {
          set({ session: abierta, aviso: null });
          return;
        }

        // Es la misma. Se traen sus totales porque puede haber vendido otra
        // tablet del mismo turno y los de aquí se quedaron viejos.
        set({ session: { ...session, ...abierta } });
      },
    }),
    {
      name:       'gc-pos-session',
      partialize: (s) => ({ session: s.session, terminal: s.terminal }),
    }
  )
);
